import * as FXP from 'fast-xml-parser';
import { topFactory } from '../../tests/factories';
import {
  ALUMINIUM_COLOR,
  BLACK_COLOR,
  CabinetRange,
  CabinetSupport,
  CrossPieceType,
  DoorHandleType,
  DoorHole,
  DrawerHandleType,
  GALVA_COLOR,
  GLASS_COLOR,
  INOX_COLOR,
  Material,
  NATURAL_COLOR,
  Position,
  PositionAngle,
  ShelfComposition,
  WHITE_COLOR,
} from '../../constants';
import { Cabinet } from '../../entities/Cabinet';
import { cornerFactory } from '../../tests/componentsFactories';
import { shelfFactory } from '../../tests/shelvesFactories';
import { findColor, getMaterialColor } from '../colorHelper';
import { Color } from '../../entities/Color';
import { getColors, getPiecesData, initializeData } from '../dataStorage';
import { Shelf } from '../../entities/Shelf';
import { Top } from '../../entities/Top';
import { arrayPieces, createCabinetPiecesList } from '../cabinetPiecesList';
import { checkAndUpdate } from '../../builder/change';

// @ts-ignore
export async function loadOldCabinet(
  id: string,
  server = 'https://www.dev.kewlox.com',
) {
  /**
   */
  const formData = new FormData();
  formData.append('action', 'getById');
  formData.append('source', 'configurator-unlocked');
  formData.append('token', '');
  formData.append('debug', '1');
  formData.append('lang', 'fr');
  formData.append('id', id);

  const options = {
    method: 'POST',
    body: formData,
  };

  try {
    const response = await fetch(`${server}/shop/kewapp/?country=BE`, options);
    if (response.ok) {
      const xml = await response.text();
      return xml;
    } else throw new Error('fetch error');
  } catch (error) {
    console.error('Error', error);
  }
}

export function dataParser(data: string, colorsData: any): Cabinet {
  const options = {
    attributeNamePrefix: '',
    attrNodeName: 'attr', //default is 'false'
    textNodeName: '#text',
    ignoreAttributes: false,
    ignoreNameSpace: false,
    allowBooleanAttributes: false,
    parseNodeValue: true,
    parseAttributeValue: false,
    trimValues: true,
    parseTrueNumberOnly: false,
    arrayMode: false, //"strict"
  };
  // @ts-ignore
  const jsonObject = FXP.parse(data, options);
  return createCabinetFromOldData(jsonObject['kewlox'], colorsData);
}

export function colorParser(colors: string) {
  const options = {
    attributeNamePrefix: '',
    attrNodeName: 'attr', //default is 'false'
    textNodeName: '#text',
    ignoreAttributes: false,
    ignoreNameSpace: false,
    allowBooleanAttributes: false,
    parseNodeValue: false,
    parseAttributeValue: false,
    trimValues: true,
    parseTrueNumberOnly: false,
    arrayMode: false, //"strict"
  };
  // @ts-ignore
  const jsonObject = FXP.parse(colors, options);
  return jsonObject['colors'];
}

function checkWeight(newWeight: number, oldWeight: number) {
  if (Math.abs(Math.round(newWeight) - Math.round(oldWeight)) > 1)
    throw new Error(
      `WEIGHT error: old = ${Math.round(oldWeight)} <> new = ${Math.round(
        newWeight,
      )}`,
    );
}

function checkPrice(newPrice: string, oldPrice: string) {
  if (parseInt(oldPrice) !== parseInt(newPrice))
    throw new Error(`PRICE error: old = ${oldPrice} <> new = ${newPrice}`);
}

function checkDecomposition(cabinet: Cabinet, oldPieces: any) {
  const cabinetPieces = createCabinetPiecesList(cabinet);
  const regexCorn = /^([0-9]{3,}-(I|G|B|N))$/g;

  const oldPiecesDic: any = {};
  for (const oldPiece of oldPieces.piece) {
    if (oldPiece.ref.match(regexCorn)) oldPiecesDic[`U${oldPiece.ref}`] = 4;
    else {
      oldPiece.ref = oldPiece.ref.replace('[ALU]', '[DIBOND]');
      oldPiecesDic[oldPiece.ref] = oldPiece.qty;
    }
  }

  const errors = [];
  const exclude = [
    'NOTE-TR',
    'VISM4X18MM',
    'VIS4X25MM',
    'VISM4X23MM',
    'VIS4X30MM',
    'WASHERM4',
    'NUTM4',
  ];

  const piecesDic = arrayPieces(cabinetPieces);
  for (const ref in oldPiecesDic) {
    if (exclude.includes(ref)) continue;
    if (!(ref in piecesDic))
      errors.push(
        `[Pieces] Miss old ref "${ref}" in converted cabinet (qty: ${oldPiecesDic[ref]})`,
      );
  }

  for (const ref in piecesDic) {
    if (exclude.includes(ref)) continue;
    if (!(ref in oldPiecesDic))
      errors.push(
        `[Pieces] Unexpected new ref "${ref}" in converted cabinet (qty: ${piecesDic[ref]})`,
      );
    else if (oldPiecesDic[ref] !== piecesDic[ref])
      errors.push(
        `[Pieces] Quantity differ for ref "${ref}" in converted cabinet (old: ${oldPiecesDic[ref]} ==> new: ${piecesDic[ref]})`,
      );
  }

  if (errors.length > 0) throw new Error(errors.toString());
}

function getColorsConverter(
  id: string,
  colors: Color[],
  colorsOldData: any,
): Color | undefined {
  const colorEntry: any = colorsOldData['color'].find(
    (e: any) => e['attr']['id'] === id,
  );

  if (colorEntry) {
    return colors.find(e => e.reference === `${colorEntry['techname']}`);
  }

  return undefined;
}

function hasGlassPlate(id: string, colorsOldData: any): boolean {
  const colorEntry: any = colorsOldData['color'].find(
    (e: any) => e['attr']['id'] === id,
  );
  return colorEntry['category'] === 'Plateau verre + acrylique';
}

function getColorsAcc(id: string, colors: Color[], colorsOldData: any) {
  const colorEntry: any = colorsOldData['color'].find(
    (e: any) => e['attr']['id'] === id,
  );
  if (colorEntry) {
    const color = colors.find(e => e.hexa === colorEntry['hexacolor']);
    if (!color) {
      const pieces = getPiecesData();
      {
        const obj = pieces.doorsHandles.getFromReference(
          colorEntry['techname'],
        );
        if (obj) {
          return obj.color;
        }
      }
      {
        const obj = pieces.drawersHandles.getFromReference(
          colorEntry['techname'],
        );
        if (obj) {
          return obj.color;
        }
      }
    } else {
      return color;
    }
  }
  return undefined;
}

function createCabinetFromOldData(oldData: any, colorsOldData: any): Cabinet {
  initializeData();
  const colors = getColors();

  const attrData = oldData['attr'];

  const cabinetInfos = {
    id: attrData['id'],
    ps_product: attrData['ps_product'],
    price: attrData['price'],
    price_without_taxes: attrData['price_without_taxes'],
    discount: attrData['discount'],
    full_price: attrData['full_price'],
    weight: attrData['weight'],
    changed_drawer: attrData['changed_drawer'],
  };

  if (cabinetInfos['id'] === '') {
    throw new Error("The product don't exist");
  }

  console.log(`Will convert cabinet ${cabinetInfos['id']}`);

  const oldPieces = oldData['pieces'];
  const descriptionData = oldData['description'];
  const descriptionAttr = descriptionData['attr'];

  const {
    width,
    depth,
    height,
    feets_type,
    feets_height,
    metal_corners,
  } = descriptionData['attr'];

  function getColorCornerColor(metal_corners: number): Color {
    switch (metal_corners) {
      case 171:
        return findColor(GALVA_COLOR);
      case 172:
        return findColor(INOX_COLOR);
      case 173:
        return findColor(BLACK_COLOR);
      case 174:
        return findColor(WHITE_COLOR);
    }
    return findColor(WHITE_COLOR);
  }

  function getCrossPieceType(id: string, colorsOldData: any): CrossPieceType {
    switch (id) {
      case '85':
        return CrossPieceType.UpperDoorsLowerDoors;
      case '87':
        return CrossPieceType.UpperEmptyLowerDoors;
      case '88':
        return CrossPieceType.UpperDoorsLowerDoors;
      case '89':
        return CrossPieceType.UpperEmptyLowerDoors;
      case '90':
        return CrossPieceType.UpperPaneLowerPane;
      case '91':
        return CrossPieceType.UpperEmptyLowerPane;
    }

    throw new Error(`This cabinet (${cabinetInfos['id']}) is not valid `);
  }

  function getFrontDrawerColor(
    oldId: string,
    colorsOldData: any,
  ): Color | undefined {
    const colorEntry: any = colorsOldData['color'].find(
      (e: any) => e['attr']['id'] === oldId,
    );

    const depreciatedColors = ['TRESPA-GR-CL', 'TRESPA-ECO', 'TRESPA'];

    if (depreciatedColors.includes(colorEntry['techname'])) {
      depreciated();
    }
    if (colorEntry['techname'] === 'ALU') {
      return findColor(ALUMINIUM_COLOR);
    }

    return colors.find(e => e.reference === `${colorEntry['techname']}`);
  }

  function getHandleDrawerType(
    id: string,
    colorsOldData: any,
  ): DrawerHandleType {
    const colorEntry: any = colorsOldData['color'].find(
      (e: any) => e['attr']['id'] === id,
    );

    if (colorEntry['typecolor'] === 'slat_d') {
      return DrawerHandleType.Slat;
    }

    if (colorEntry['typecolor'] === 'pull') return DrawerHandleType.Knop;
    if (colorEntry['typecolor'] === 'button') return DrawerHandleType.Button;
    // else if (
    //   colorEntry['typecolor'] === 'drawer' &&
    //   colorEntry['techname'] === 'eco'
    // )
    //   return DrawerHandleType.Eco;
    // else if (
    //   colorEntry['typecolor'] === 'drawer' &&
    //   colorEntry['techname'] === 'eco+'
    // )
    //   return DrawerHandleType.EcoPlus;
    else if (colorEntry['typecolor'] === 'drawer')
      return DrawerHandleType.Button;

    throw new Error(`This cabinet (${cabinetInfos['id']}) is not valid`);
  }

  function convertCombo(specOption: number): number[] {
    /**
     * DRAWER_27(1004,27),
     * DRAWER_10_17(1005,27),
     * DRAWER_13_13(1006,27)
    ,DRAWER_37(1007,37),
     DRAWER_17_20(1008,37),
     DRAWER_10_10_17(1009,37),
     DRAWER_10_13_13(1010,37),
     DRAWER_10_27(1011,37)
    ,DRAWER_10_37(1012,47),
     DRAWER_20_27(1013,47),
     DRAWER_10_10_27(1014,47),
     DRAWER_10_17_20(1015,47),
     DRAWER_10_10_10_17(1016,47),
     DRAWER_10_10_13_13(1017,47)
    ,DRAWER_10(1018,10)
     */
    let combo = undefined;
    switch (specOption) {
      case 1004:
        combo = [270];
        break;
      case 1005:
        combo = [100, 170];
        break;
      case 1006:
        combo = [135, 135];
        break;
      case 1007:
        combo = [370];
        break;
      case 1008:
        combo = [170, 200];
        break;
      case 1009:
        combo = [100, 100, 170];
        break;
      case 1010:
        combo = [100, 135, 135];
        break;
      case 1011:
        combo = [100, 270];
        break;
      case 1012:
        combo = [100, 370];
        break;
      case 1013:
        combo = [200, 270];
        break;
      case 1014:
        combo = [100, 100, 270];
        break;
      case 1015:
        combo = [100, 170, 200];
        break;
      case 1016:
        combo = [100, 100, 100, 170];
        break;
      case 1017:
        combo = [100, 100, 135, 135];
        break;
      case 1018:
        combo = [100];
        break;
      default:
        throw new Error(`This cabinet (${cabinetInfos['id']}) is not valid `);
    }

    return combo;
  }

  const cornerColor = getColorCornerColor(parseInt(metal_corners));

  const cabinet = new Cabinet(
    parseInt(width),
    parseInt(depth),
    CabinetRange.Standard,
    [
      cornerFactory(PositionAngle.FrontLeft, parseInt(height), cornerColor),
      cornerFactory(PositionAngle.FrontRight, parseInt(height), cornerColor),
      cornerFactory(PositionAngle.BackLeft, parseInt(height), cornerColor),
      cornerFactory(PositionAngle.BackRight, parseInt(height), cornerColor),
    ],
    [],
    topFactory(parseInt(width), parseInt(depth)),
  );

  // Support
  // FEET_NONE(0),WOOD(1),ALU_VAR(2),ALU_FIX(3),HANG(4),WHEEL_WH(5),WHEEL_BL(6);
  switch (parseInt(feets_type)) {
    case 0:
      cabinet.setSupport(CabinetSupport.NoSupport);
      break;
    case 1:
      cabinet.setSupport(
        CabinetSupport.Feet,
        parseInt(feets_height),
        Material.Wood,
      );
      break;
    case 2:
      cabinet.setSupport(
        CabinetSupport.AdjustableFeet,
        parseInt(feets_height),
        Material.Aluminium,
      );
      break;
    case 3:
      cabinet.setSupport(
        CabinetSupport.Feet,
        parseInt(feets_height),
        Material.Aluminium,
      );
      break;
    case 4:
      cabinet.setSupport(CabinetSupport.ToHangHook);
      break;
    case 5:
      cabinet.setSupport(CabinetSupport.WheelWhite);
      break;
    case 6:
      cabinet.setSupport(CabinetSupport.WheelBlack);
      break;
    default:
      cabinet.setSupport(CabinetSupport.NoSupport);
  }

  function notValid(): void {
    throw new Error(`This cabinet (${cabinetInfos['id']}) is not valid `);
  }

  function depreciated(): void {
    throw new Error(`This cabinet (${cabinetInfos['id']}) is depreciated`);
  }

  function setDoor(
    shelf: Shelf,
    oldDoorId: string,
    knopId: string,
    position: PositionAngle,
  ) {
    if (oldDoorId && oldDoorId !== '0') {
      const doorColor = getColorsConverter(oldDoorId, colors, colorsOldData);
      if (!doorColor)
        throw new Error(`This cabinet (${cabinetInfos['id']}) is not valid `);
      shelf.updateDoor(
        position,
        undefined,
        undefined,
        getMaterialColor(doorColor),
        undefined,
        doorColor,
      );
    } else {
      notValid();
    }

    if (knopId && knopId !== '0') {
      if (knopId === '183') {
        shelf.addDoorHandle(position, DoorHandleType.Button);
      } else if (knopId === '184') {
        shelf.addDoorHandle(position, DoorHandleType.BigButton);
      } else if (knopId === '196') {
        shelf.addDoorHandle(position, DoorHandleType.Slat);
        shelf.setLargeDoorSlat(position, Material.Wood);
      } else if (knopId === '197') {
        shelf.addDoorHandle(position, DoorHandleType.Slat);
        shelf.setLargeDoorSlat(position, Material.Aluminium);
      } else if (knopId === '0') {
        shelf.removeDoorHandle(position);
        shelf.updateDoor(
          position,
          undefined,
          undefined,
          undefined,
          DoorHole.cupLarge,
        );
      } else {
        const color = getColorsAcc(knopId, colors, colorsOldData);
        if (!color)
          throw new Error(`This cabinet (${cabinetInfos['id']}) is not valid `);
        shelf.addDoorHandle(position, DoorHandleType.Knop, color);
      }
    } else {
      shelf.removeDoorHandle(position);
      shelf.updateDoor(
        position,
        undefined,
        undefined,
        undefined,
        DoorHole.cupLarge,
      );
    }
  }

  function setSide(shelf: Shelf, oldId: string, position: Position) {
    if (oldId && oldId !== '0') {
      const color = getColorsConverter(oldId, colors, colorsOldData);
      if (!color)
        throw new Error(`This cabinet (${cabinetInfos['id']}) is not valid `);
      for (const rack of shelf.racks) {
        rack.updatePane(
          position,
          undefined,
          undefined,
          getMaterialColor(color),
          color,
        );
      }
    } else {
      for (const rack of shelf.racks) {
        rack.removePane(position);
      }
    }
  }

  function setPlate(shelf: Shelf, oldId: string) {
    if (oldId === '0') {
      for (const rack of shelf.racks) {
        rack.plate = undefined;
      }
      return;
    }
    const color = getColorsConverter(oldId, colors, colorsOldData);
    if (!color)
      throw new Error(`This cabinet (${cabinetInfos['id']}) is not valid `);
    for (const rack of shelf.racks) {
      rack.updatePlate(
        undefined,
        undefined,
        getMaterialColor(color),
        color,
        hasGlassPlate(oldId, colorsOldData),
      );
    }
  }

  function setTopPlate(top: Top, depth: number, oldId: string) {
    if (oldId !== '0') {
      const color = getColorsConverter(oldId, colors, colorsOldData);
      if (!color)
        throw new Error(`This cabinet (${cabinetInfos['id']}) is not valid `);
      top.updatePlate(
        depth,
        undefined,
        getMaterialColor(color),
        color,
        hasGlassPlate(oldId, colorsOldData),
      );
    } else {
      top.plate = undefined;
      top.glassPlate = undefined;
    }
  }

  function setCrossPiece(
    shelf: Shelf,
    oldId: string,
    position: Position,
    width: number,
  ) {
    if (oldId === '0') {
      for (const rack of shelf.racks) {
        rack.removeCrossPiece(position);
      }
    } else {
      const type = getCrossPieceType(oldId, colorsOldData);
      const reinforce = oldId === '88' || oldId === '89';
      for (const rack of shelf.racks) {
        rack.updateCrossPiece(
          position,
          width,
          Material.Wood,
          type,
          rack.level > 0 ? false : reinforce,
        );
      }
    }
  }

  function setTopCrossPiece(
    top: Top,
    oldId: string,
    position: Position,
    width: number,
  ) {
    if (oldId !== '0') {
      const type = getCrossPieceType(oldId, colorsOldData);
      const reinforce = oldId === '88' || oldId === '89';
      top.updateCrossPiece(position, width, Material.Wood, type, reinforce);
    } else {
      top.removeCrossPiece(position);
    }
  }

  for (const shelfEntry of descriptionData['shelf']) {
    if (shelfEntry['attr']['pos'] !== 'last') {
      const shelf = shelfFactory(
        parseInt(shelfEntry['attr']['pos']),
        370,
        cabinet,
      );
      cabinet.shelves.push(shelf);
      cabinet.updateCornerHeight();
      // Height
      shelf.height = parseInt(shelfEntry['spec']['attr']['value']);
      // Composition
      if (shelfEntry['spec_type']['attr']['value'] === '2') {
        shelf.composition = ShelfComposition.Open;
      } else {
        setDoor(
          shelf,
          shelfEntry['spec_doorleft']['attr']['value'],
          shelfEntry['spec_coupleft']['attr']['value'],
          PositionAngle.FrontLeft,
        );
        setDoor(
          shelf,
          shelfEntry['spec_doorright']['attr']['value'],
          shelfEntry['spec_coupright']['attr']['value'],
          PositionAngle.FrontRight,
        );
      }

      const specOption = parseInt(shelfEntry['spec_option']['attr']['value']);

      // Drawers
      if (specOption >= 1004 && specOption <= 1018) {
        shelf.composition = ShelfComposition.Drawer;

        const combo = convertCombo(specOption);
        shelf.setDrawersCombination(combo.reverse());

        const handleDrawerId = shelfEntry['spec_coupleft']['attr']['value'];

        // Front drawer
        const colorFrontDrawer = getFrontDrawerColor(
          shelfEntry['spec_doorright']['attr']['value'],
          colorsOldData,
        );

        for (let i = 0; i < shelf.drawers.length; i++) {
          shelf.updateDrawer(
            i,
            undefined,
            undefined,
            undefined,
            colorFrontDrawer,
          );
        }

        if (
          colorFrontDrawer?.reference !== 'ECO' &&
          colorFrontDrawer?.reference !== 'BRUT-ECO' &&
          colorFrontDrawer?.reference !== 'ECO-PLUS'
        ) {
          const drawerHandleType = getHandleDrawerType(
            handleDrawerId,
            colorsOldData,
          );
          const colorDrawerHandle = getColorsAcc(
            handleDrawerId,
            colors,
            colorsOldData,
          );
          for (const position of Array.from(
            Array(shelf.drawers.length).keys(),
          )) {
            shelf.addDrawerHandle(
              position,
              drawerHandleType,
              colorDrawerHandle,
            );
          }
        }
      } else if (
        specOption >= 4 &&
        specOption <= 35 &&
        specOption !== 19 &&
        specOption !== 20
      ) {
        depreciated();
      }

      // Panes
      setSide(
        shelf,
        shelfEntry['spec_leftside']['attr']['value'],
        Position.Left,
      );
      setSide(
        shelf,
        shelfEntry['spec_rightside']['attr']['value'],
        Position.Right,
      );
      setSide(
        shelf,
        shelfEntry['spec_backside']['attr']['value'],
        Position.Back,
      );

      setPlate(shelf, shelfEntry['spec_shelfpane']['attr']['value']);

      // Inter Shelf
      let isColor = undefined;
      switch (specOption) {
        case 1:
          isColor = findColor(WHITE_COLOR);
          break;
        case 2:
          isColor = findColor(BLACK_COLOR);
          break;
        case 19:
          isColor = findColor(GLASS_COLOR);
          break;
        case 20:
          isColor = findColor(NATURAL_COLOR);
          break;
      }
      if (isColor) {
        // Set color of support equals to inter shelf because it was the old model
        shelf.addIntermediateShelf(
          undefined,
          undefined,
          isColor,
          isColor,
          isColor,
        );
      }

      if (shelfEntry['spec_option2']['attr']['value'] !== '0') {
        // OPTION_NONE(0),TROU_CABLE(1),DOS_COURT(2);
        depreciated();
      }

      setCrossPiece(
        shelf,
        shelfEntry['spec_frontrail']['attr']['value'],
        Position.Front,
        shelf.cabinet.width,
      );
      setCrossPiece(
        shelf,
        shelfEntry['spec_backrail']['attr']['value'],
        Position.Back,
        shelf.cabinet.width,
      );
      setCrossPiece(
        shelf,
        shelfEntry['spec_rightrail']['attr']['value'],
        Position.Right,
        shelf.cabinet.depth,
      );
      setCrossPiece(
        shelf,
        shelfEntry['spec_leftrail']['attr']['value'],
        Position.Left,
        shelf.cabinet.depth,
      );
    } else {
      // Top Cabinet
      setTopPlate(
        cabinet.top,
        cabinet.depth,
        shelfEntry['spec_shelfpane']['attr']['value'],
      );

      setTopCrossPiece(
        cabinet.top,
        shelfEntry['spec_frontrail']['attr']['value'],
        Position.Front,
        cabinet.width,
      );
      setTopCrossPiece(
        cabinet.top,
        shelfEntry['spec_backrail']['attr']['value'],
        Position.Back,
        cabinet.width,
      );
      setTopCrossPiece(
        cabinet.top,
        shelfEntry['spec_rightrail']['attr']['value'],
        Position.Right,
        cabinet.depth,
      );
      setTopCrossPiece(
        cabinet.top,
        shelfEntry['spec_leftrail']['attr']['value'],
        Position.Left,
        cabinet.depth,
      );
    }
  }

  checkAndUpdate(cabinet);

  // Set cross pieces like old cabinet (checkAndUpdate could have make unwanted changes)
  for (const shelfEntry of descriptionData['shelf']) {
    if (shelfEntry['attr']['pos'] !== 'last') {
      const shelf = cabinet.shelf(parseInt(shelfEntry['attr']['pos']));
      setCrossPiece(
        shelf,
        shelfEntry['spec_frontrail']['attr']['value'],
        Position.Front,
        shelf.cabinet.width,
      );
      setCrossPiece(
        shelf,
        shelfEntry['spec_backrail']['attr']['value'],
        Position.Back,
        shelf.cabinet.width,
      );
      setCrossPiece(
        shelf,
        shelfEntry['spec_rightrail']['attr']['value'],
        Position.Right,
        shelf.cabinet.depth,
      );
      setCrossPiece(
        shelf,
        shelfEntry['spec_leftrail']['attr']['value'],
        Position.Left,
        shelf.cabinet.depth,
      );
    } else {
      setTopPlate(
        cabinet.top,
        cabinet.depth,
        shelfEntry['spec_shelfpane']['attr']['value'],
      );

      setTopCrossPiece(
        cabinet.top,
        shelfEntry['spec_frontrail']['attr']['value'],
        Position.Front,
        cabinet.width,
      );
      setTopCrossPiece(
        cabinet.top,
        shelfEntry['spec_backrail']['attr']['value'],
        Position.Back,
        cabinet.width,
      );
      setTopCrossPiece(
        cabinet.top,
        shelfEntry['spec_rightrail']['attr']['value'],
        Position.Right,
        cabinet.depth,
      );
      setTopCrossPiece(
        cabinet.top,
        shelfEntry['spec_leftrail']['attr']['value'],
        Position.Left,
        cabinet.depth,
      );
    }
  }

  // Now, check without updates
  try {
    checkAndUpdate(cabinet, false);
  } catch (e) {
    console.error(e.toString());
  }

  try {
    checkPrice(cabinet.displayPriceTaxInclude(), cabinetInfos['price']);
  } catch (e) {
    console.error(e.toString());
  }

  try {
    checkWeight(cabinet.weight, parseFloat(cabinetInfos['weight']));
  } catch (e) {
    console.error(e.toString());
  }

  try {
    checkDecomposition(cabinet, oldPieces);
  } catch (e) {
    const errorDecomposition = e.toString().replace(/,/g, '\n');
    console.error(errorDecomposition);
  }

  return cabinet;
}
